import React, { useEffect, useState } from "react";
import { Visit, VisitService, Transaction } from "../../interfaces/Visit";
import {
  UpdateVisitField,
  changeVisit,
  changeVisitData,
  selectVisit,
} from "../../features/visit/visitSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getPaymentSummary,
  updateVisit,
  getVisitTransactions,
} from "../../apis/visit";
import { useNotification } from "../../NotificationContext";
import ServiceSelect from "./ServiceSelect";
import Modal from "../../components/Modal";
import AddService from "../services/AddService";
import AddVisitTransaction from "./AddVisitTransaction";
import { getPackages, getPackageById } from "../../apis/packages";
import MultiSelect from "../../../src/components/MultiSelect";

interface PharmacyItem {
  id?: number;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface LaboratoryItem {
  id?: number;
  name: string;
  fee: number;
}

interface InsuranceInfo {
  provider: string;
  policyNumber: string;
  coverageAmount: number;
  claimNumber: string;
}

const Payment: React.FC<{
  preview?: boolean;
}> = ({ preview = false }) => {
  const [transactions, setTransactions] = useState<Array<Transaction>>([]);
  const [activeTab, setActiveTab] = useState<
    "services" | "pharmacy" | "laboratory"
  >("services");
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [editableTransactionId, setEditableTransactionId] = useState<
    number | null
  >(null);
  const visit = useAppSelector(selectVisit);
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [serviceText, setServiceText] = useState("");

  // Insurance state
  const [insuranceInfo, setInsuranceInfo] = useState<InsuranceInfo>({
    provider: "",
    policyNumber: "",
    coverageAmount: 0,
    claimNumber: "",
  });
  const [isInsuranceEnabled, setIsInsuranceEnabled] = useState(false);

  // Pharmacy state
  const [pharmacyItems, setPharmacyItems] = useState<Array<PharmacyItem>>([]);
  const [newPharmacyItem, setNewPharmacyItem] = useState<PharmacyItem>({
    name: "",
    quantity: 1,
    unitPrice: 0,
    total: 0,
  });

  // Laboratory state
  const [laboratoryItems, setLaboratoryItems] = useState<Array<LaboratoryItem>>(
    []
  );
  const [newLabItem, setNewLabItem] = useState<LaboratoryItem>({
    name: "",
    fee: 0,
  });
  const [packages, setPackages] = useState<any[]>([]);
const [selectedPackageId, setSelectedPackageId] = useState<number | "">("");
const [selectedPackages, setSelectedPackages] = useState<any[]>([]);
  useEffect(() => {
    if (!preview && visit.id) {
      fetchSummary();
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit.id, preview]);

  useEffect(() => {
    // Calculate pharmacy item total when quantity or unitPrice changes
    setNewPharmacyItem((prev) => ({
      ...prev,
      total: (prev.quantity || 0) * (prev.unitPrice || 0),
    }));
  }, [newPharmacyItem.quantity, newPharmacyItem.unitPrice]);

  const updateField = (fieldName: keyof Visit, value: any) => {
    const payload: UpdateVisitField = { fieldName, value };
    dispatch(changeVisitData(payload));
  };

  useEffect(() => {
  fetchPackages();
}, []);

const fetchPackages = async () => {
  try {
    const res: any = await getPackages({});
    setPackages(res?.data || []);
  } catch (err) {
    console.log(err);
  }
};

const handlePackageChange = async (packageId: number) => {
  setSelectedPackageId(packageId);
  try {
    const res: any = await getPackageById(packageId);
    const packageServices = res?.data?.package_services || [];
    const mappedServices = packageServices.map((ps: any) => ({
      id: ps.service.id,
      name: ps.service.name,
      fee: ps.service.fee,
      quantity: 1,
    }));
    const updatedServices = [...visit.services, ...mappedServices];
    updateField("services", updatedServices);
    updateVisitData({ ...visit, services: updatedServices });
  } catch (err) {
    console.log(err);
  }
};

  const fetchSummary = () => {
    if (visit.id) {
      getPaymentSummary(visit.id).then((res: any) => {
        // Payment summary can be used for additional data if needed
        // Currently using transactions directly for calculations
      }).catch(console.error);
    }
  };

  const fetchTransactions = () => {
    if (visit.id) {
      getVisitTransactions(visit.id).then((res: any) => {
        setTransactions(res.data.data || []);
      }).catch(console.error);
    }
  };

  // Calculate totals
  const getServicesTotal = () => {
    return visit.services.reduce((a, b) => {
      const fee = parseFloat(String(b.fee ?? 0));
      const quantity = parseFloat(String(b.quantity ?? 0));
      return a + fee * quantity;
    }, 0);
  };

  const getPharmacyTotal = () => {
    return pharmacyItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const getLaboratoryTotal = () => {
    return laboratoryItems.reduce((sum, item) => sum + (item.fee || 0), 0);
  };

  const getGrandTotal = () => {
    return getServicesTotal() + getPharmacyTotal() + getLaboratoryTotal();
  };

  const getTotalPaid = () => {
    return transactions.reduce(
      (sum, txn) => sum + (parseFloat(String(txn.amount)) || 0),
      0
    );
  };

  const getDiscountAmount = () => {
    return parseFloat(String(visit.discount || 0));
  };

  const getInsuranceCoverage = () => {
    return isInsuranceEnabled
      ? parseFloat(String(insuranceInfo.coverageAmount || 0))
      : 0;
  };

  const getAmountToBePaid = () => {
    const total = getGrandTotal();
    const discount = getDiscountAmount();
    const insurance = getInsuranceCoverage();
    const paid = getTotalPaid();
    return Math.max(0, total - discount - insurance - paid);
  };

  const getAmountDue = () => {
    const total = getGrandTotal();
    const discount = getDiscountAmount();
    const insurance = getInsuranceCoverage();
    const paid = getTotalPaid();
    const paying = parseFloat(String(visit.amountPaid || 0));
    return Math.max(0, total - discount - insurance - paid - paying);
  };

  const calculatePayingAmount = (services: Array<VisitService>) => {
    // This will be handled by the payment summary
  };

  const handleAddQuantity = (service: VisitService) => {
    let visitServices: Array<VisitService> = visit.services;
    let updatedServices: Array<VisitService> = visitServices.map((i) => {
      if (i.id === service.id) {
        return { ...i, quantity: (i.quantity || 0) + 1 };
      }
      return i;
    });
    updateField("services", updatedServices);
    updateVisitData({ ...visit, services: updatedServices });
  };

  const handleSubtractQuantity = (service: VisitService) => {
    let visitServices: Array<VisitService> = visit.services;
    let updatedServices: Array<VisitService> = visitServices.map((i) => {
      if (i.id === service.id) {
        if ((i.quantity || 0) > 1) {
          return { ...i, quantity: (i.quantity || 0) - 1 };
        } else {
          showNotification(
            "error",
            "Quantity cannot be less than 1",
            "",
            "topRight"
          );
          return i;
        }
      }
      return i;
    });
    updateField("services", updatedServices);
    updateVisitData({ ...visit, services: updatedServices });
  };

  const updateVisitData = async (payload: any) => {
    updateVisit(payload).then((res: any) => {
      dispatch(changeVisit(res));
      fetchSummary();
    });
  };

  const handleDiscount = (discount: any) => {
    const discountValue = parseFloat(discount) || 0;
    updateField("discount", discountValue);
    updateVisitData({ ...visit, discount: discountValue });
  };

  const handlePaying = (amount: any) => {
    const payingAmount = parseFloat(amount) || 0;
    updateField("amountPaid", payingAmount);
  };

  const showServiceModal = (value: string) => {
    setServiceText(value);
    setIsServiceModalOpen(true);
  };

  // Pharmacy handlers
  const handleAddPharmacyItem = () => {
    if (newPharmacyItem.name && newPharmacyItem.unitPrice > 0) {
      const newItem: PharmacyItem = {
        ...newPharmacyItem,
        id: Date.now(), // Temporary ID
      };
      setPharmacyItems([...pharmacyItems, newItem]);
      setNewPharmacyItem({
        name: "",
        quantity: 1,
        unitPrice: 0,
        total: 0,
      });
    } else {
      showNotification(
        "error",
        "Please fill all pharmacy item fields",
        "",
        "topRight"
      );
    }
  };

  const handleRemovePharmacyItem = (id: number | undefined) => {
    if (id) {
      setPharmacyItems(pharmacyItems.filter((item) => item.id !== id));
    }
  };

  // Laboratory handlers
  const handleAddLabItem = () => {
    if (newLabItem.name && newLabItem.fee > 0) {
      const newItem: LaboratoryItem = {
        ...newLabItem,
        id: Date.now(), // Temporary ID
      };
      setLaboratoryItems([...laboratoryItems, newItem]);
      setNewLabItem({
        name: "",
        fee: 0,
      });
    } else {
      showNotification(
        "error",
        "Please fill all laboratory item fields",
        "",
        "topRight"
      );
    }
  };

  const handleRemoveLabItem = (id: number | undefined) => {
    if (id) {
      setLaboratoryItems(laboratoryItems.filter((item) => item.id !== id));
    }
  };

  // Transaction handlers
  const handleAddTransaction = () => {
    setEditableTransactionId(null);
    setIsTransactionModalOpen(true);
  };

  const handleEditTransaction = (transactionId: number) => {
    setEditableTransactionId(transactionId);
    setIsTransactionModalOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setIsTransactionModalOpen(false);
    setEditableTransactionId(null);
    fetchTransactions();
    fetchSummary();
  };

  // Insurance handlers
  const handleInsuranceToggle = (enabled: boolean) => {
    setIsInsuranceEnabled(enabled);
    if (!enabled) {
      setInsuranceInfo({
        provider: "",
        policyNumber: "",
        coverageAmount: 0,
        claimNumber: "",
      });
    }
  };

  const handleInsuranceChange = (field: keyof InsuranceInfo, value: any) => {
    setInsuranceInfo({
      ...insuranceInfo,
      [field]: field === "coverageAmount" ? parseFloat(value) || 0 : value,
    });
  };

  return (
    <div className="p-4 space-y-6 w-full">
      {/* Tabs for Services, Pharmacy, Laboratory */}
      <div className="bg-white rounded-lg border border-gray-300 shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("services")}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === "services"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab("pharmacy")}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === "pharmacy"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Pharmacy
            </button>
            <button
              onClick={() => setActiveTab("laboratory")}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === "laboratory"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Laboratory
            </button>
          </nav>
        </div>

        <div className="block px-4 relative z-50 overflow-visible">
          <label className="block text-sm font-medium text-gray-700">
            Select Package
          </label>
          <MultiSelect
            selectedOptions={selectedPackages}
            options={packages.map((pkg) => ({ value: pkg.id, label: pkg.name }))}
            placeholder="Select Packages..."
            onSearch={() => {}}
            onAddNewOption={() => {}}
            onOptionSelect={(option) => {
              if (!selectedPackages.find((p) => p.value === option.value)) {
                setSelectedPackages([...selectedPackages, option]);

                const matchedPackage = packages.find((p) => p.id === option.value);
                if (matchedPackage?.package_services) {
                  const newServices = matchedPackage.package_services.map((ps: any) => ({
                    id: ps.service.id,
                    name: ps.service.name,
                    fee: ps.service.fee,
                    quantity: 1,
                  }));

                  const updatedServices = [
                    ...visit.services,
                    ...newServices.filter(
                      (ns: any) => !visit.services.find((s) => s.id === ns.id)
                    ),
                  ];

                  updateField("services", updatedServices);
                  updateVisitData({ ...visit, services: updatedServices });
                }
              }
            }}
            onOptionRemove={(option) => {
              setSelectedPackages(selectedPackages.filter((p) => p.value !== option.value));

              const matchedPackage = packages.find((p) => p.id === option.value);
              if (matchedPackage?.package_services) {
                const remainingServices = visit.services.filter(
                  (s) => !matchedPackage.package_services.some((ps: any) => ps.service.id === s.id)
                );
                updateField("services", remainingServices);
                updateVisitData({ ...visit, services: remainingServices });
              }
            }}
          />
        </div>

        {/* Services Tab */}
        {activeTab === "services" && (
          <div className="p-4 space-y-4">
            <div className="block">
              <span className="text-sm font-medium text-gray-700">
                Select Service
              </span>
              <ServiceSelect
                visit={visit}
                onCreate={(value) => showServiceModal(value)}
                onServiceChange={(services) => calculatePayingAmount(services)}
              />
            </div>
            <div className="overflow-x-auto relative bg-white rounded-lg border border-gray-300">
              <label className="bg-[#ECEFFF] w-full px-4 py-2 text-base font-medium border-b block">
                Services & Charges
              </label>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visit.services && visit.services.length > 0 ? (
                    visit.services.map((service) => (
                      <tr key={service.id}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {service.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex gap-2 items-center">
                            <button
                              onClick={() => handleSubtractQuantity(service)}
                              className="p-1 text-gray-600 hover:text-gray-900"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </button>
                            <span className="px-2 min-w-[2rem] text-center">
                              {service.quantity || 0}
                            </span>
                            <button
                              onClick={() => handleAddQuantity(service)}
                              className="p-1 text-gray-600 hover:text-gray-900"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ₹
                          {(
                            (service?.fee ?? 0) * (service?.quantity ?? 0)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-4 text-sm text-center text-gray-500"
                      >
                        No services added
                      </td>
                    </tr>
                  )}
                  <tr className="font-semibold bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      ₹{getServicesTotal().toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pharmacy Tab */}
        {activeTab === "pharmacy" && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Medicine Name
                </label>
                <input
                  type="text"
                  value={newPharmacyItem.name}
                  onChange={(e) =>
                    setNewPharmacyItem({
                      ...newPharmacyItem,
                      name: e.target.value,
                    })
                  }
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                  placeholder="Enter medicine name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={newPharmacyItem.quantity}
                  onChange={(e) =>
                    setNewPharmacyItem({
                      ...newPharmacyItem,
                      quantity: parseInt(e.target.value) || 1,
                    })
                  }
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Unit Price
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPharmacyItem.unitPrice}
                  onChange={(e) =>
                    setNewPharmacyItem({
                      ...newPharmacyItem,
                      unitPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleAddPharmacyItem}
                  className="px-4 py-2 w-full text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="overflow-x-auto relative bg-white rounded-lg border border-gray-300">
              <label className="bg-[#ECEFFF] w-full px-4 py-2 text-base font-medium border-b block">
                Pharmacy Items
              </label>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Medicine
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Unit Price
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pharmacyItems.length > 0 ? (
                    pharmacyItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ₹{item.unitPrice.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ₹{item.total.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleRemovePharmacyItem(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 text-sm text-center text-gray-500"
                      >
                        No pharmacy items added
                      </td>
                    </tr>
                  )}
                  {pharmacyItems.length > 0 && (
                    <tr className="font-semibold bg-gray-50">
                      <td
                        colSpan={3}
                        className="px-4 py-3 text-sm text-gray-900"
                      >
                        Total
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{getPharmacyTotal().toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Laboratory Tab */}
        {activeTab === "laboratory" && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Test Name
                </label>
                <input
                  type="text"
                  value={newLabItem.name}
                  onChange={(e) =>
                    setNewLabItem({ ...newLabItem, name: e.target.value })
                  }
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                  placeholder="Enter test name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Fee
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newLabItem.fee}
                  onChange={(e) =>
                    setNewLabItem({
                      ...newLabItem,
                      fee: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleAddLabItem}
                  className="px-4 py-2 w-full text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
            </div>

            <div className="overflow-x-auto relative bg-white rounded-lg border border-gray-300">
              <label className="bg-[#ECEFFF] w-full px-4 py-2 text-base font-medium border-b block">
                Laboratory Tests
              </label>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Test Name
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Fee
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {laboratoryItems.length > 0 ? (
                    laboratoryItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ₹{item.fee.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleRemoveLabItem(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-4 text-sm text-center text-gray-500"
                      >
                        No laboratory tests added
                      </td>
                    </tr>
                  )}
                  {laboratoryItems.length > 0 && (
                    <tr className="font-semibold bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{getLaboratoryTotal().toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-4 w-full lg:grid-cols-3">
        {/* Left Column - Payment Inputs */}
        <div className="space-y-4 lg:col-span-2">
          {/* Insurance Section */}
          <div className="p-4 bg-white rounded-lg border border-gray-300 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <label className="text-base font-medium text-gray-900">
                Insurance Payment
              </label>
              <label className="inline-flex relative items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isInsuranceEnabled}
                  onChange={(e) => handleInsuranceToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
            {isInsuranceEnabled && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Insurance Provider
                  </label>
                  <input
                    type="text"
                    value={insuranceInfo.provider}
                    onChange={(e) =>
                      handleInsuranceChange("provider", e.target.value)
                    }
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    placeholder="Provider name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Policy Number
                  </label>
                  <input
                    type="text"
                    value={insuranceInfo.policyNumber}
                    onChange={(e) =>
                      handleInsuranceChange("policyNumber", e.target.value)
                    }
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    placeholder="Policy number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Coverage Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={insuranceInfo.coverageAmount}
                    onChange={(e) =>
                      handleInsuranceChange("coverageAmount", e.target.value)
                    }
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Claim Number
                  </label>
                  <input
                    type="text"
                    value={insuranceInfo.claimNumber}
                    onChange={(e) =>
                      handleInsuranceChange("claimNumber", e.target.value)
                    }
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    placeholder="Claim number"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Discount and Paying */}
          <div className="p-4 bg-white rounded-lg border border-gray-300 shadow-md">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Discount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={visit.discount || 0}
                  onChange={(e) => handleDiscount(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Paying Now (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={visit.amountPaid || 0}
                  onChange={(e) => handlePaying(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Payment Summary */}
        <div className="bg-white rounded-lg border border-gray-300 shadow-md">
          <label className="bg-[#ECEFFF] w-full text-base font-medium border-b py-2 px-4 block">
            Payment Summary
          </label>
          <div className="flex flex-col p-5 space-y-3">
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Services Total
              </span>
              <span className="text-sm font-medium text-gray-900">
                ₹{getServicesTotal().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Pharmacy Total
              </span>
              <span className="text-sm font-medium text-gray-900">
                ₹{getPharmacyTotal().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Laboratory Total
              </span>
              <span className="text-sm font-medium text-gray-900">
                ₹{getLaboratoryTotal().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b-2 border-gray-300">
              <span className="text-base font-semibold text-gray-900">
                Grand Total
              </span>
              <span className="text-base font-semibold text-gray-900">
                ₹{getGrandTotal().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-sm text-gray-600">Discount</span>
              <span className="text-sm text-indigo-700">
                -₹{getDiscountAmount().toFixed(2)}
              </span>
            </div>
            {isInsuranceEnabled && (
              <div className="flex justify-between py-1">
                <span className="text-sm text-gray-600">
                  Insurance Coverage
                </span>
                <span className="text-sm text-indigo-700">
                  -₹{getInsuranceCoverage().toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between py-1">
              <span className="text-sm text-gray-600">Amount Paid</span>
              <span className="text-sm text-indigo-700">
                ₹{getTotalPaid().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1 pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Amount To Be Paid
              </span>
              <span className="text-sm font-medium text-green-700">
                ₹{getAmountToBePaid().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-sm font-medium text-gray-700">
                Paying Now
              </span>
              <span className="text-sm font-medium text-green-700">
                ₹{parseFloat(String(visit.amountPaid || 0)).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-2 pt-2 border-t-2 border-gray-300">
              <span className="text-base font-semibold text-gray-900">
                Amount Due
              </span>
              <span
                className={`text-base font-semibold ${
                  getAmountDue() > 0 ? "text-red-700" : "text-green-700"
                }`}
              >
                ₹{getAmountDue().toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isServiceModalOpen && (
        <Modal
          modalContent={
            <AddService
              text={serviceText}
              editableId={null}
              closeModal={() => setIsServiceModalOpen(false)}
            />
          }
        />
      )}

      {isTransactionModalOpen && (
        <Modal
          modalContent={
            <AddVisitTransaction
              visitId={visit.id}
              editableId={editableTransactionId}
              closeModal={handleCloseTransactionModal}
            />
          }
        />
      )}
    </div>
  );
};

export default Payment;
